import { useState } from "react";
import axios from "axios";
import { Loader2, ArrowRight, Check, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Visual bullets shown both in the teaser AND inside the modal.
// Order mirrors the actual map flow (Portada → Cierre).
const MAP_INCLUDES = [
  "Cómo juegas bajo presión",
  "Tu ventaja estratégica",
  "Tu modo sombra",
  "Tus patrones bajo presión",
  "Regulación emocional",
  "Las 4 etapas de evolución",
  "Slider interactivo",
  "Reflexiones prácticas",
  "Cierre personalizado de tu avatar",
];

/**
 * MapaCompletoSection — premium "Tu mapa completo" block.
 *
 * Renders BOTH:
 *  (1) An inline section under the free result (teaser + bullets + form),
 *      always visible on /r/:id and on /mapa-completo/:id.
 *  (2) A "Ver detalle completo" trigger that opens a focused modal with
 *      the same content (useful when arriving from email links).
 *
 * Props:
 *  - lead: full lead object (must include id, name).
 *  - source: "resultado_test" | "email_3" | "email_5".
 *  - autoOpenModal: open the modal on mount (used by /mapa-completo route).
 */
export default function MapaCompletoSection({ lead, source = "resultado_test", autoOpenModal = false }) {
  const [open, setOpen] = useState(Boolean(autoOpenModal));

  return (
    <>
      <InlineTeaser lead={lead} source={source} onOpenModal={() => setOpen(true)} />
      {open && (
        <ModalDialog onClose={() => setOpen(false)}>
          <ModalContent lead={lead} source={source} onClose={() => setOpen(false)} />
        </ModalDialog>
      )}
    </>
  );
}

/* ----------------------------- Inline teaser ----------------------------- */

function InlineTeaser({ lead, source, onOpenModal }) {
  return (
    <section
      data-testid="mapa-completo-section"
      className="relative mt-20 md:mt-24 border-t border-white/8 pt-16 md:pt-24"
    >
      {/* Personalised opener */}
      <div className="max-w-3xl mx-auto px-6 md:px-12 space-y-6 text-center">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#29BC9B]">
          Acceso premium · PLAY YOUR GAME™
        </div>
        <h2 className="font-display text-3xl md:text-5xl leading-[1.1]">
          {lead?.name ? `${lead.name}, tu mapa completo` : "Tu mapa completo"}
          <br className="hidden md:block" />
          <span className="text-white/60">va mucho más profundo.</span>
        </h2>
        <p className="text-white/65 leading-relaxed text-base md:text-lg max-w-2xl mx-auto">
          Tu resultado gratuito solo muestra <em>cómo</em> juegas bajo presión.
          El mapa completo te ayuda a entender cuándo tu fortaleza empieza a
          jugar en tu contra, cómo aparece tu modo sombra y cómo regular tu
          ventaja sin dejar de ser quien eres.
        </p>
      </div>

      {/* Bullets — premium editorial grid */}
      <div className="max-w-4xl mx-auto px-6 md:px-12 mt-12 md:mt-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-3">
          {MAP_INCLUDES.map((item) => (
            <div
              key={item}
              className="flex items-start gap-3 py-3 border-b border-white/5"
              data-testid="mapa-includes-item"
            >
              <span className="mt-1 w-4 h-4 rounded-full bg-[#29BC9B]/15 border border-[#29BC9B]/40 flex items-center justify-center shrink-0">
                <Check className="w-2.5 h-2.5 text-[#29BC9B]" strokeWidth={3} />
              </span>
              <span className="text-white/80 text-sm md:text-base">{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Inline WhatsApp form */}
      <div className="max-w-2xl mx-auto px-6 md:px-12 mt-16 md:mt-20">
        <WhatsAppForm
          lead={lead}
          source={source}
          variant="inline"
          onOpenModal={onOpenModal}
        />
      </div>
    </section>
  );
}

/* -------------------------------- Modal -------------------------------- */

function ModalDialog({ onClose, children }) {
  return (
    <div
      className="fixed inset-0 z-[100] flex items-end md:items-center justify-center bg-black/80 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
      data-testid="mapa-completo-modal"
    >
      <div
        className="relative w-full md:max-w-2xl bg-[#080808] border border-white/10 md:rounded-sm shadow-[0_20px_60px_-20px_rgba(0,0,0,0.9)] my-0 md:my-12 animate-[fadeUp_0.35s_ease]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Cerrar"
          data-testid="mapa-completo-modal-close"
          className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full border border-white/10 bg-black/40 hover:border-[#29BC9B]/40 hover:text-[#29BC9B] text-white/60 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        {children}
      </div>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

function ModalContent({ lead, source, onClose }) {
  return (
    <div className="px-6 md:px-12 py-12 md:py-16 text-white">
      <div className="text-[10px] uppercase tracking-[0.4em] text-[#29BC9B] mb-5">
        Acceso premium
      </div>
      <h3 className="font-display text-3xl md:text-4xl leading-[1.1] mb-5">
        Tu mapa completo
        <br />
        <span className="text-white/60">va mucho más profundo.</span>
      </h3>
      <p className="text-white/70 leading-relaxed text-sm md:text-base mb-8">
        Te contactaré personalmente por WhatsApp en las próximas horas para
        darte acceso a tu mapa.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 mb-8">
        {MAP_INCLUDES.map((item) => (
          <div key={item} className="flex items-start gap-2 py-2">
            <Check className="w-3.5 h-3.5 text-[#29BC9B] mt-1 shrink-0" strokeWidth={3} />
            <span className="text-white/80 text-xs md:text-sm">{item}</span>
          </div>
        ))}
      </div>

      <WhatsAppForm lead={lead} source={source} variant="modal" onSuccess={onClose} />
    </div>
  );
}

/* ----------------------------- WhatsApp form ---------------------------- */

function WhatsAppForm({ lead, source, variant = "inline", onSuccess, onOpenModal }) {
  const [whatsapp, setWhatsapp] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const value = whatsapp.trim();
    if (value.length < 6) {
      toast.error("Introduce un WhatsApp válido");
      return;
    }
    if (!lead?.id) {
      toast.error("No se pudo identificar el lead");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads/${lead.id}/map-interest`, {
        whatsapp: value,
        source,
      });
      setDone(true);
      toast.success("Recibido. Te contactaré por WhatsApp.");
      if (typeof onSuccess === "function") {
        setTimeout(onSuccess, 2200);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "No se pudo registrar tu solicitud";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        className="border border-[#29BC9B]/30 bg-[#29BC9B]/[0.04] rounded-sm p-6 md:p-8 text-center"
        data-testid="mapa-completo-success"
      >
        <div className="inline-flex w-10 h-10 rounded-full bg-[#29BC9B]/15 border border-[#29BC9B]/40 items-center justify-center mb-4">
          <Check className="w-5 h-5 text-[#29BC9B]" strokeWidth={3} />
        </div>
        <h4 className="font-display text-xl md:text-2xl mb-2">
          {lead?.name ? `Gracias, ${lead.name}.` : "Gracias."}
        </h4>
        <p className="text-white/65 text-sm leading-relaxed">
          Recibí tu interés. Te contactaré personalmente por WhatsApp en las
          próximas horas con el acceso a tu mapa completo.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className={
        variant === "modal"
          ? "space-y-4"
          : "space-y-5 border border-white/10 bg-[#0A0A0A] rounded-sm p-6 md:p-8"
      }
      data-testid="mapa-completo-form"
    >
      <div className="space-y-1.5">
        <h4 className="font-display text-xl md:text-2xl">
          ¿Quieres acceder a tu mapa completo?
        </h4>
        <p className="text-white/55 text-sm">
          Déjanos tu WhatsApp y te contactaré personalmente.
        </p>
      </div>

      <div>
        <label htmlFor={`wa-${variant}`} className="block text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">
          WhatsApp / Teléfono
        </label>
        <input
          id={`wa-${variant}`}
          name="whatsapp"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="+34 600 123 456"
          value={whatsapp}
          onChange={(e) => setWhatsapp(e.target.value)}
          disabled={submitting}
          required
          data-testid="mapa-completo-input"
          className="w-full bg-transparent border border-white/15 focus:border-[#29BC9B] focus:outline-none text-white placeholder:text-white/25 px-4 py-3 rounded-sm text-base transition-colors disabled:opacity-50"
        />
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid="mapa-completo-submit"
        className="w-full inline-flex items-center justify-center gap-2 bg-[#29BC9B] hover:bg-[#22a487] text-black font-bold tracking-[0.15em] text-xs px-6 py-3.5 rounded-sm uppercase transition-colors disabled:opacity-60 disabled:cursor-wait"
      >
        {submitting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> Enviando…
          </>
        ) : (
          <>
            Quiero mi mapa completo <ArrowRight className="w-4 h-4" />
          </>
        )}
      </button>

      {variant === "inline" && typeof onOpenModal === "function" && (
        <button
          type="button"
          onClick={onOpenModal}
          data-testid="mapa-completo-open-modal"
          className="block w-full text-center text-[10px] uppercase tracking-[0.3em] text-white/35 hover:text-[#29BC9B] transition-colors pt-2"
        >
          Ver detalle completo
        </button>
      )}
    </form>
  );
}
